import { NavigationGuardNext, RouteLocationNormalized, RouteRecordRaw } from 'vue-router'
import { adminAuthGuard } from '@/router/clients/cfs/cfsRouteGuards.ts'
import { adviceRouteGuard } from '@/composables/adviceRouteGuard.ts'
import { adviceAuthGuard } from '@/router/routeGuards.ts'
import { IUserStore } from '@/store/pinia/UserStore.ts'
import { IAccountFlagsStore } from '@/store/pinia/AccountFlagsStore.ts'
import { ISessionStore } from '@/store/pinia/SessionStore.ts'
// Lazy loaded debt components
const DebtManagementView = () => import('@/views/Otivo/Dashboard/Debt/DebtManagementView.vue')
const DebtDetails = () => import('@/views/Otivo/Dashboard/Debt/Pages/DebtDetails.vue')
const DebtDetailsOverview = () => import('@/views/Otivo/Dashboard/Debt/Pages/DebtDetailsOverview.vue')
import {
  ExclusionRules,
  useWhitelabelExclusionRules,
} from '@/composables/useWhitelabelExclusionRules.ts'
import isReturningLegacyUser from '@/composables/users/isReturningLegacyUser.ts'
const Paywall = () => import('@/components/CreateAccount/Paywall/Paywall.vue')
import { useFreemium } from '@/composables/freemium/useFreemium.ts'
import { FeatureList, useLDFlag } from '@/lib/ldClient.ts'
import { type AdviceModuleType } from '@/types/adviceModules'

const AdviceModule = () => import('@/app/components/module-upsells/AdviceModule.vue')

export default (
  userStore: IUserStore,
  sessionStore: ISessionStore,
  accountFlagsStore: IAccountFlagsStore,
) => {
  const { hasExclusionRule } = useWhitelabelExclusionRules()
  const enableDebtRoutes = !hasExclusionRule(ExclusionRules.DEBT_MODULE)
  const enableRetirementRoutes =
    !hasExclusionRule(ExclusionRules.RETIREMENT_PLAN) &&
    useLDFlag(FeatureList.ENABLE_RETIREMENT_PLAN, false).value
  const { freemiumEnabled } = useFreemium()

  const paywallGuard = (next: NavigationGuardNext): boolean => {
    const user = userStore.getUser
    if (!sessionStore.getWhitelabelData.subscription_required) return true
    if (isReturningLegacyUser() || !user.has_consented) return true
    if (user?.subscriptions.length > 0 && user.active_subscription) return true
    if (freemiumEnabled.value) return true

    next({ name: 'Paywall' })
    return false
  }

  const baseRoutes: Array<RouteRecordRaw> = [
    {
      path: '/dashboard',
      component: () => import('@/views/AdviceView.vue'),
      beforeEnter: (to, from, next) => adviceAuthGuard(to, from, next, userStore),
      children: [
        {
          path: '',
          name: 'clientDashboard',
          component: () => import('@/views/ClientDashboard.vue'),
        },
        {
          path: 'how-the-advice-works/:adviceTargetName',
          name: 'howTheAdviceWorksPage',
          component: () => import('@/components/CFS/HowTheAdviceWorksPage.vue'),
        },
        {
          path: 'how-advice-works',
          name: 'HowAdviceWorks',
          component: () => import('@/components/CFS/CFSClientConfirmationSteps/HowAdviceWorks.vue'),
        },
        {
          path: 'details',
          name: 'clientDetails',
          component: () => import('@/components/CFS/Guidance/UserProfile.vue'),
        },
        {
          path: 'financial-summary',
          name: 'clientFinancialSummary',
          component: () => import('@/components/FinancialSummary/FinancialSummary.vue'),
        },
        {
          path: 'member-summary',
          name: 'guidanceMemberSummary',
          component: () => import('@/components/CFS/Guidance/GuidanceMemberSummary.vue'),
          beforeEnter: async (to, from, next) => {
            if (
              sessionStore.getWhitelabelData.callcentre_portal ||
              sessionStore.getWhitelabelData.admin_portal
            ) {
              await adminAuthGuard(to, from, next, userStore, sessionStore)
            } else next(from)
          },
        },
        {
          path: 'insurance-expenses',
          name: 'insuranceExpenses',
          component: () => import('@/views/Otivo/Dashboard/Insurance/InsuranceExpenses.vue'),
          beforeEnter: async (
            to: RouteLocationNormalized,
            from: RouteLocationNormalized,
            next: NavigationGuardNext,
          ) => {
            if (typeof to.name === 'string' && userStore.getUser?.user_role === 'client') {
              const insuranceCompleted =
                !!accountFlagsStore?.getFlag('personalInsurance')?.questions_completed
              if (!insuranceCompleted) {
                next({ name: 'clientDashboard' })
              }
            }
            next()
          },
        },
        {
          path: 'super-investments',
          name: 'superInvestments',
          component: AdviceModule,
          props: { module: 'superInvestments' as AdviceModuleType },
          beforeEnter: (to, from, next) =>
            adviceRouteGuard(to, from, next, userStore, accountFlagsStore, sessionStore),
        },
        {
          path: 'salary-sacrifice-contributions',
          name: 'salarySacrificeContributions',
          component: AdviceModule,
          props: { module: 'salarySacrificeContributions' as AdviceModuleType },
          beforeEnter: (to, from, next) => {
            if (paywallGuard(next)) {
              adviceRouteGuard(to, from, next, userStore, accountFlagsStore, sessionStore)
            }
          },
        },
        {
          path: 'lump-sum-concessional-contributions',
          name: 'lumpSumConcessionalContributions',
          component: AdviceModule,
          props: { module: 'lumpSumConcessionalContributions' as AdviceModuleType, type: 'concessional' },
          beforeEnter: (to, from, next) => {
            if (paywallGuard(next)) {
              adviceRouteGuard(to, from, next, userStore, accountFlagsStore, sessionStore)
            }
          },
        },
        {
          path: 'lump-sum-non-concessional-contributions',
          name: 'lumpSumNonConcessionalContributions',
          component: AdviceModule,
          props: { module: 'lumpSumConcessionalContributions' as AdviceModuleType, type: 'non_concessional' },
          beforeEnter: (to, from, next) => {
            if (paywallGuard(next)) {
              adviceRouteGuard(to, from, next, userStore, accountFlagsStore, sessionStore)
            }
          },
        },
        {
          path: 'personal-insurance',
          name: 'personalInsurance',
          component: AdviceModule,
          props: { module: 'personalInsurance' as AdviceModuleType },
          beforeEnter: (to, from, next) => {
            if (paywallGuard(next)) {
              adviceRouteGuard(to, from, next, userStore, accountFlagsStore, sessionStore)
            }
          },
        },
        {
          path: 'concessional-contributions-guide',
          name: 'guidanceConcessionalContributionsGuide',
          component: () => import('@/components/Modals/HowToCheckConcessionalContributions.vue'),
        },
        {
          path: '/non-concessional-contributions-guide',
          name: 'clientNonConcessionalContributionsGuide',
          component: () => import('@/components/Modals/HowToCheckNonConcessionalContributions.vue'),
        },
        {
          path: 'subscribe',
          name: 'Paywall',
          component: Paywall,
          beforeEnter: (to, from, next) => {
            const whitelabel = sessionStore.getWhitelabelData
            if (whitelabel.subscription_required && !userStore.getUser.active_subscription) next()
            else next({ name: 'clientDashboard' })
          },
        },
      ],
    },
    {
      path: '/dashboard',
      redirect: '/dashboard',
    },
  ]
  const debtRoutes: Array<RouteRecordRaw> = [
    {
      path: 'debt-plan',
      name: 'debtPlan',
      component: AdviceModule,
      props: { module: 'debtPlan' as AdviceModuleType },
      beforeEnter: (to, from, next) => {
        if (paywallGuard(next)) {
          adviceRouteGuard(to, from, next, userStore, accountFlagsStore, sessionStore)
        }
      },
    },
    {
      path: 'debt',
      name: 'DebtManagement',
      component: DebtManagementView,
      children: [
        {
          path: ':type/:id',
          name: 'DebtDetails',
          component: DebtDetails,
        },
        {
          path: 'overview/:type/:id',
          name: 'DebtDetailsOverview',
          component: DebtDetailsOverview,
        },
      ],
    },
  ]
  const retirementRoutes: Array<RouteRecordRaw> = [
    {
      path: 'retirement-plan',
      name: 'retirementPlan',
      component: AdviceModule,
      props: { module: 'retirementPlan' as AdviceModuleType },
      beforeEnter: (to, from, next) => {
        if (paywallGuard(next)) {
          adviceRouteGuard(to, from, next, userStore, accountFlagsStore, sessionStore)
        }
      },
    },
  ]

  const routes: Array<RouteRecordRaw> = [...baseRoutes]

  if (enableDebtRoutes) routes[0]?.children?.push(...debtRoutes)
  if (enableRetirementRoutes) routes[0]?.children?.push(...retirementRoutes)

  return routes
}
